import type {
    RPCCall,
    Channel,
    ChatMessage,
    ParticipantID,
} from '@pexip/plugin-api';

import {infinityService} from '../services/InfinityClient.service';
import type {Meeting} from '../services/InfinityClient.service';

const replyWithError = (data: RPCCall, channel: Channel, error: unknown) => {
    const message =
        error instanceof Error ? error.message : 'Unknown error occurred';
    channel.replyRPCError({rpc: data.rpc, replyTo: data.id, error: message});
};

export const handleInfinityCall = ({
    data,
    channel,
    meeting,
}: {
    data: RPCCall;
    channel: Channel;
    meeting: Meeting;
}) => {
    if (data.rpc === 'conference:dialOut') {
        infinityService
            .dial(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:sendMessage') {
        // FIXME: we have a conundrum here, if we use the sendMessage of the meeting service, we lose the result
        // if we use the sendMessage directly from the infinity client, we don't update the service state
        // this is a general issue throughout all our handling of API requests.
        meeting.sendMessage(data.payload.payload, data.payload.participantUuid);
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: {status: 200, data: {result: true, status: 'success'}},
        });
    } else if (data.rpc === 'conference:sendApplicationMessage') {
        infinityService
            .sendApplicationMessage(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:lock') {
        infinityService
            .lock(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:muteAllGuests') {
        infinityService
            .muteAllGuests(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:setBandwidth') {
        infinityService.setBandwidth(data.payload);
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: undefined,
        });
    } else if (data.rpc === 'conference:setLayout') {
        infinityService
            .setLayout(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:disconnectAll') {
        infinityService
            .disconnectAll(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:sendRequest') {
        infinityService
            .sendConferenceRequest(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:requestParticipants') {
        infinityService
            .requestParticipants(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:breakout') {
        infinityService
            .breakout(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:joinBreakoutRoom') {
        infinityService
            .joinBreakoutRoom(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:closeBreakouts') {
        infinityService
            .closeBreakouts()
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:closeBreakoutRoom') {
        infinityService
            .closeBreakoutRoom(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:emptyBreakouts') {
        infinityService
            .emptyBreakouts()
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:breakoutMoveParticipants') {
        infinityService
            .breakoutMoveParticipants(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'conference:currentRoomId') {
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: infinityService.roomId,
        });
    } else if (data.rpc === 'conference:getChatMessages') {
        const chatMessages = meeting
            .getChatMessages()
            .filter(msg => msg.type === 'user-message') satisfies ChatMessage[];
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: chatMessages,
        });
    } else if (data.rpc === 'conference:getDirectChatMessages') {
        const directChatMessages = meeting.getDirectChatMessages();
        const requestedMessages = new Map<ParticipantID, ChatMessage[]>(
            data.payload.map(participantId => [
                participantId,
                directChatMessages.get(participantId) ?? [],
            ]),
        );
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: requestedMessages satisfies Map<
                ParticipantID,
                ChatMessage[]
            >,
        });
    } else if (data.rpc === 'conference:getLiveCaptions') {
        const liveCaptions = meeting.getLiveCaptions();
        channel.replyRPC({
            rpc: data.rpc,
            replyTo: data.id,
            payload: liveCaptions,
        });
    } else if (data.rpc === 'participant:transfer') {
        infinityService
            .transfer(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:mute') {
        infinityService
            .mute(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:muteVideo') {
        infinityService
            .muteVideo(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:spotlight') {
        infinityService
            .spotlight(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:admit') {
        infinityService
            .admit(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:raiseHand') {
        infinityService
            .raiseHand(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:setRole') {
        infinityService
            .setRole(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:setTextOverlay') {
        infinityService
            .setTextOverlay(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:sendDTMF') {
        infinityService
            .sendDTMF(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    } else if (data.rpc === 'participant:disconnect') {
        infinityService
            .kick(data.payload)
            .then(result => {
                channel.replyRPC({
                    rpc: data.rpc,
                    replyTo: data.id,
                    payload: result,
                });
            })
            .catch(error => {
                replyWithError(data, channel, error);
            });
    }
};
